<template>
  <div v-if="show" class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h2>{{ $t('userManagement.importUsers') }}</h2>
        <button @click="$emit('close')" class="close-btn">&times;</button>
      </div>
      
      <div class="modal-body">
        <div class="instructions">
          <h3>{{ $t('userManagement.importInstructions') }}</h3>
          <p>{{ $t('userManagement.csvFormatDescription') }}</p>
          
          <div class="csv-example">
            <strong>{{ $t('userManagement.exampleFormat') }}:</strong>
            <pre>first_name,last_name,username,email,role,phone,date_of_birth,social_security_number,address,employment_date,password,employment_type,employment_rate,employment_hours_per_week,monthly_salary,hourly_salary,employee_tax_table
John,Doe,johnd,john@example.com,worker,0701234567,1990-05-15,199005151234,123 Main St,2024-01-01,temp123,monthly,100,40,35000,,33
Jane,Smith,janes,jane@example.com,worker,0709876543,1985-03-20,198503201234,456 Oak Ave,2024-01-15,temp456,hourly,80,32,,185,32</pre>
          </div>
          
          <button @click="downloadTemplate" class="btn-secondary">
            📥 {{ $t('userManagement.downloadTemplate') }}
          </button>
        </div>
        
        <div class="file-upload">
          <label for="csv-file" class="file-label">
            {{ $t('userManagement.selectCsvFile') }}
          </label>
          <input 
            type="file" 
            id="csv-file" 
            accept=".csv"
            @change="handleFileSelect"
            class="file-input"
          />
          <span v-if="selectedFile" class="file-name">{{ selectedFile.name }}</span>
        </div>
        
        <div v-if="preview.length > 0" class="preview-section">
          <h3>{{ $t('userManagement.preview') }} ({{ preview.length }} {{ $t('userManagement.users') }})</h3>
          <div class="preview-table-wrapper">
            <table class="preview-table">
              <thead>
                <tr>
                  <th>{{ $t('userProfile.firstName') }}</th>
                  <th>{{ $t('userProfile.lastName') }}</th>
                  <th>{{ $t('userProfile.username') }}</th>
                  <th>{{ $t('userProfile.email') }}</th>
                  <th>{{ $t('userProfile.role') }}</th>
                  <th>{{ $t('userProfile.employmentType') }}</th>
                  <th>{{ $t('common.status') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(user, index) in preview" :key="index" :class="{ 'row-error': user.error }">
                  <td>{{ user.first_name }}</td>
                  <td>{{ user.last_name }}</td>
                  <td>{{ user.username }}</td>
                  <td>{{ user.email }}</td>
                  <td>{{ user.role }}</td>
                  <td>{{ user.employment_type }}</td>
                  <td>
                    <span v-if="user.error" class="error-badge">{{ user.error }}</span>
                    <span v-else class="success-badge">✓</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <div v-if="errors.length > 0" class="error-list">
          <h4>{{ $t('common.error') }}:</h4>
          <ul>
            <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
          </ul>
        </div>
        
        <div v-if="importResults" class="results">
          <h3>{{ $t('userManagement.importResults') }}</h3>
          <p class="success-text">✓ {{ importResults.success }} {{ $t('userManagement.usersImported') }}</p>
          <p v-if="importResults.failed > 0" class="error-text">✗ {{ importResults.failed }} {{ $t('userManagement.usersFailed') }}</p>
          <ul v-if="importResults.errors.length > 0">
            <li v-for="(error, index) in importResults.errors" :key="index">{{ error }}</li>
          </ul>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="$emit('close')" class="btn-secondary">
          {{ $t('common.cancel') }}
        </button>
        <button 
          @click="importUsers" 
          :disabled="loading || preview.length === 0"
          class="btn-primary"
        >
          {{ loading ? $t('common.saving') : $t('userManagement.importUsers') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close', 'imported'])

const { t } = useI18n()
const authStore = useAuthStore()

const selectedFile = ref(null)
const preview = ref([])
const errors = ref([])
const loading = ref(false)
const importResults = ref(null)

function handleFileSelect(event) {
  const file = event.target.files[0]
  if (!file) return
  
  selectedFile.value = file
  errors.value = []
  importResults.value = null
  
  const reader = new FileReader()
  reader.onload = (e) => {
    try {
      parseCSV(e.target.result)
    } catch (error) {
      errors.value.push(error.message)
    }
  }
  reader.readAsText(file)
}

function parseCSV(csvText) {
  const lines = csvText.trim().split('\n')
  if (lines.length < 2) {
    throw new Error(t('userManagement.csvEmpty'))
  }
  
  const headers = lines[0].split(',').map(h => h.trim())
  const requiredFields = ['first_name', 'last_name', 'username', 'password', 'role']
  
  // Check required headers
  const missingFields = requiredFields.filter(field => !headers.includes(field))
  if (missingFields.length > 0) {
    throw new Error(`${t('userManagement.missingFields')}: ${missingFields.join(', ')}`)
  }
  
  preview.value = []
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue
    
    const values = line.split(',').map(v => v.trim())
    const user = {}
    
    headers.forEach((header, index) => {
      user[header] = values[index] || null
    })
    
    // Basic validation
    if (!user.first_name || !user.last_name || !user.username) {
      user.error = t('userManagement.missingRequiredData')
    } else if (!user.password || user.password.length < 8) {
      user.error = t('auth.passwordTooShort')
    } else if (!['worker', 'planner', 'manager'].includes(user.role)) {
      user.error = t('userManagement.invalidRole')
    }
    
    preview.value.push(user)
  }
}

function downloadTemplate() {
  const headers = [
    'first_name', 'last_name', 'username', 'email', 'role', 'phone',
    'date_of_birth', 'social_security_number', 'address', 'employment_date', 'password',
    'employment_type', 'employment_rate', 'employment_hours_per_week',
    'monthly_salary', 'hourly_salary', 'employee_tax_table'
  ]
  
  const example = [
    'John', 'Doe', 'johnd', 'john@example.com', 'worker', '0701234567',
    '1990-05-15', '199005151234', '123 Main St', '2024-01-01', 'temp123',
    'full_time', '100', '40', '35000', '', '33'
  ]
  
  const csv = [
    headers.join(','),
    example.join(',')
  ].join('\n')
  
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'user_import_template.csv'
  a.click()
  URL.revokeObjectURL(url)
}

async function importUsers() {
  loading.value = true
  errors.value = []
  
  const results = {
    success: 0,
    failed: 0,
    errors: []
  }
  
  for (const user of preview.value) {
    if (user.error) {
      results.failed++
      results.errors.push(`${user.username}: ${user.error}`)
      continue
    }
    
    try {
      // Create user
      const userData = {
        first_name: user.first_name,
        last_name: user.last_name,
        username: user.username,
        email: user.email || null,
        role: user.role,
        phone: user.phone || null,
        date_of_birth: user.date_of_birth || null,
        social_security_number: user.social_security_number || null,
        address: user.address || null,
        employment_date: user.employment_date || null,
        password: user.password
      }
      
      const response = await authStore.makeAuthenticatedRequest('/api/users/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || errorData.message || 'Failed to create user')
      }
      
      const createdUser = await response.json()
      
      // Create payroll if salary info provided
      if (user.employment_type || user.monthly_salary || user.hourly_salary) {
        const payrollData = {
          user: createdUser.id,
          employment_type: user.employment_type || 'monthly',
          employment_rate: parseFloat(user.employment_rate) || 100,
          employment_hours_per_week: parseFloat(user.employment_hours_per_week) || null,
          monthly_salary: parseFloat(user.monthly_salary) || null,
          hourly_salary: parseFloat(user.hourly_salary) || null,
          employee_tax_table: parseInt(user.employee_tax_table) || null
        }
        
        const payrollResponse = await authStore.makeAuthenticatedRequest('/api/payroll/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payrollData)
        })
        
        if (!payrollResponse.ok) {
          results.errors.push(`${user.username}: User created but payroll failed`)
        }
      }
      
      results.success++
    } catch (error) {
      results.failed++
      results.errors.push(`${user.username}: ${error.message}`)
    }
  }
  
  importResults.value = results
  loading.value = false
  
  if (results.success > 0) {
    emit('imported')
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: #999;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.instructions {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.instructions h3 {
  margin-top: 0;
  color: #333;
}

.csv-example {
  background: white;
  padding: 1rem;
  border-radius: 4px;
  margin: 1rem 0;
}

.csv-example pre {
  overflow-x: auto;
  font-size: 0.85rem;
  margin: 0.5rem 0 0;
}

.file-upload {
  margin: 1.5rem 0;
}

.file-label {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: #667eea;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  margin-right: 1rem;
}

.file-input {
  display: none;
}

.file-name {
  color: #666;
  font-style: italic;
}

.preview-section {
  margin: 1.5rem 0;
}

.preview-table-wrapper {
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.preview-table th,
.preview-table td {
  padding: 0.5rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.preview-table th {
  background: #f8f9fa;
  font-weight: 600;
  position: sticky;
  top: 0;
}

.row-error {
  background: #fee;
}

.error-badge {
  color: #c33;
  font-size: 0.85rem;
}

.success-badge {
  color: #3c3;
}

.error-list {
  background: #fee;
  padding: 1rem;
  border-radius: 6px;
  margin: 1rem 0;
}

.error-list h4 {
  margin-top: 0;
  color: #c33;
}

.error-list ul {
  margin: 0;
  padding-left: 1.5rem;
}

.results {
  background: #efe;
  padding: 1rem;
  border-radius: 6px;
  margin: 1rem 0;
}

.results h3 {
  margin-top: 0;
  color: #333;
}

.success-text {
  color: #3c3;
  font-weight: 600;
}

.error-text {
  color: #c33;
  font-weight: 600;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #eee;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #e0e0e0;
  color: #333;
}

.btn-secondary:hover {
  background: #d0d0d0;
}
</style>
